import { put, takeEvery } from 'redux-saga/effects';
import { orderBy } from 'lodash';

import { GET_DEFAULT_RECOMMENDATIONS_SUCCESS } from 'actionTypes';
import { setSummaries } from 'actions';
import highlightingService from 'services/highlightingService';

const TAG_SCHEMAS = [
  'categories',
  'subcategories',
  'universities',
  'provinces',
  'cities',
];

const TOP_TAGS_OPTIONS = {
  categories: { limit: 5, calculateOther: true },
  subcategories: { limit: 15, calculateOther: false },
  universities: { limit: 5, calculateOther: true },
  provinces: { limit: 15, calculateOther: false },
  cities: { limit: 15, calculateOther: false },
};

const MAX_RECOMMENDATIONS = 100;

function getSchemaCount(recommendation) {
  let schemaCounts = TAG_SCHEMAS.reduce((map, schema) => {
    map[schema] = 0;
    return map;
  }, {});

  recommendation.tags.forEach(tag => {
    schemaCounts[tag.schema] += 1;
  });
  return schemaCounts;
}

function getTagsInAllRecommendations(action) {
  const { entities, entities: { recommendations } } = action.normalizedData;
  let tagsInRecommendation = TAG_SCHEMAS.reduce((map, schema) => {
    map[schema] = [];
    return map;
  }, {});

  Object.values(recommendations)
    .splice(0, MAX_RECOMMENDATIONS)
    .forEach(recommendation => {
      // we need to find out how many tags with this schema appear in the
      // recommendation so the weightedCount of each tag can be properly calculated
      const schemaCount = getSchemaCount(recommendation);

      recommendation.tags.forEach(tagInfo => {
        tagsInRecommendation[tagInfo.schema].push({
          ...entities[tagInfo.schema][tagInfo.id],
          schemaCount: schemaCount[tagInfo.schema],
        });
      });
    });
  return tagsInRecommendation;
}

function getSchemaSummary(tagsInAllRecommendations, schema) {
  const schemaTags = tagsInAllRecommendations[schema];

  let summaryObj = {};

  schemaTags.forEach(tag => {
    if (!summaryObj[tag.id]) {
      summaryObj[tag.id] = {
        weightedCount: 1 / tag.schemaCount,
        count: 1,
        tag: {
          id: tag.id,
          schema: tag.schema,
        },
      };
    } else {
      summaryObj[tag.id].weightedCount += 1 / tag.schemaCount;
      summaryObj[tag.id].count += 1;
    }
  });
  return summaryObj;
}

function calculateTopList(
  summary,
  recommendationsCount,
  schema,
  { limit, calculateOther }
) {
  let sortedTopList = orderBy(
    Object.values(summary),
    ['weightedCount', 'name'],
    ['desc', 'asc']
  );

  if (limit) {
    sortedTopList = sortedTopList.splice(0, limit);
  }

  return sortedTopList;
}

function calculateRecommendationsTotal(recommendations) {
  return Math.min(Object.keys(recommendations).length, MAX_RECOMMENDATIONS);
}

function* summarySaga(action) {
  const { entities: { recommendations } } = action.normalizedData;
  let summaries;
  if (!recommendations) {
    return yield put(setSummaries(summaries));
  }
  const tagsInAllRecommendations = getTagsInAllRecommendations(action);
  summaries = {};
  let topTags = {};
  TAG_SCHEMAS.forEach(schema => {
    const schemaSummary = getSchemaSummary(tagsInAllRecommendations, schema);
    summaries[schema] = schemaSummary;

    topTags[schema] = calculateTopList(
      schemaSummary,
      recommendations.length,
      schema,
      TOP_TAGS_OPTIONS[schema]
    );
  });

  highlightingService.registerSummary(
    recommendations,
    tagsInAllRecommendations,
    topTags
  );
  summaries.topTags = topTags;
  summaries.topRecommendationsTotal = calculateRecommendationsTotal(
    recommendations
  );
  yield put(setSummaries(summaries));
}

export default [takeEvery(GET_DEFAULT_RECOMMENDATIONS_SUCCESS, summarySaga)];



// WEBPACK FOOTER //
// ./src/sagas/summarySagas.js